import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, Award, Shield, CheckCircle, Clock, AlertCircle, Lock, Edit2, Save, X } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { put } from '@/lib/api';

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  preferred_name?: string;
  email: string;
  personal_email?: string;
  phone?: string;
  employment_type: string;
  employment_status: string;
  department_name?: string;
  title: string;
  manager_name?: string;
  hire_date: string;
  start_date?: string;
  location?: string;
  remote: boolean;
  emergency_contacts: Array<{ name: string; relationship: string; phone: string }>;
  certifications: Array<{ name: string; issuer: string; expiry_date?: string; status: string }>;
  skills: string[];
  notes?: string;
  compensation?: { salary: number; currency: string; pay_frequency: string };
}

interface PolicyAck {
  policy_id: string;
  title: string;
  status: 'signed' | 'pending' | 'overdue';
  signed_at: string | null;
}

export function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'info' | 'policies'>('info');
  const { data: employee, loading, refetch } = useApi<Employee>(`employees/${id}`);
  const { data: policiesData } = useApi<{ policies: PolicyAck[] }>(`legal/policies/employee-status?employee_id=${id ?? ''}`, [id]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    preferred_name: '',
    email: '',
    personal_email: '',
    phone: '',
    title: '',
    department_name: '',
    manager_name: '',
    location: '',
    remote: false,
    employment_type: 'full_time',
    skills: '',
    notes: '',
  });

  function startEditing() {
    if (!employee) return;
    setEditForm({
      first_name: employee.first_name ?? '',
      last_name: employee.last_name ?? '',
      preferred_name: employee.preferred_name ?? '',
      email: employee.email ?? '',
      personal_email: employee.personal_email ?? '',
      phone: employee.phone ?? '',
      title: employee.title ?? '',
      department_name: employee.department_name ?? '',
      manager_name: employee.manager_name ?? '',
      location: employee.location ?? '',
      remote: employee.remote ?? false,
      employment_type: employee.employment_type ?? 'full_time',
      skills: (employee.skills ?? []).join(', '),
      notes: employee.notes ?? '',
    });
    setEditing(true);
    setEditError(null);
  }

  async function handleSave() {
    if (!editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.email.trim()) {
      setEditError('First name, last name, and email are required.');
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      await put(`employees/${id}`, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        preferred_name: editForm.preferred_name || undefined,
        email: editForm.email.trim(),
        personal_email: editForm.personal_email || undefined,
        phone: editForm.phone || undefined,
        title: editForm.title || undefined,
        department_name: editForm.department_name || undefined,
        manager_name: editForm.manager_name || undefined,
        location: editForm.location || undefined,
        remote: editForm.remote,
        employment_type: editForm.employment_type,
        skills: editForm.skills ? editForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        notes: editForm.notes || undefined,
      });
      setEditing(false);
      refetch();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-muted-foreground">Loading...</div>;
  if (!employee) return <div className="text-muted-foreground">Employee not found</div>;

  const inputClass = 'w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/employees')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to Employees
        </button>
        <div className="flex items-center gap-2">
          {!editing ? (
            <button onClick={startEditing} className="flex items-center gap-2 text-sm px-3 py-1.5 border border-border text-foreground rounded-md hover:bg-surface-2">
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
          ) : (
            <>
              <button onClick={() => setEditing(false)} className="flex items-center gap-2 text-sm px-3 py-1.5 text-muted-foreground rounded-md hover:bg-surface-2">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button onClick={() => void handleSave()} disabled={saving} className="flex items-center gap-2 text-sm px-3 py-1.5 bg-amber-500 text-foreground rounded-md hover:bg-amber-600 disabled:opacity-50">
                <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
          <Link to={`/employees/${id}/sensitive-info`} className="flex items-center gap-2 text-sm px-3 py-1.5 border border-amber-500/30 text-amber-600 rounded-md hover:bg-amber-500/10">
            <Lock className="w-3.5 h-3.5" /> Sensitive Info
          </Link>
        </div>
      </div>

      {editError && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">{editError}</div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border mb-2">
        <button
          onClick={() => setActiveTab('info')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'info' ? 'border-amber-500 text-amber-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Profile
        </button>
        <button
          onClick={() => setActiveTab('policies')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'policies' ? 'border-amber-500 text-amber-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <Shield className="w-3.5 h-3.5" />
          Policies
          {policiesData && policiesData.policies.filter(p => p.status !== 'signed').length > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-red-500/10 text-red-400">
              {policiesData.policies.filter(p => p.status !== 'signed').length}
            </span>
          )}
        </button>
      </div>

      {/* Policies Tab */}
      {activeTab === 'policies' && (
        <div className="space-y-4">
          {!policiesData || policiesData.policies.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No policies assigned.</p>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="divide-y divide-border">
                {policiesData.policies.map(p => (
                  <div key={p.policy_id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {p.status === 'signed' && <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />}
                      {p.status === 'pending' && <Clock className="w-4 h-4 text-amber-500 shrink-0" />}
                      {p.status === 'overdue' && <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />}
                      <span className="text-sm text-foreground truncate">{p.title}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {p.status === 'signed' && p.signed_at && (
                        <span className="text-xs text-muted-foreground">{new Date(p.signed_at).toLocaleDateString()}</span>
                      )}
                      {p.status !== 'signed' && (
                        <Link
                          to={`/policies/${p.policy_id}/sign`}
                          className="text-xs text-amber-500 hover:text-amber-400 font-medium"
                        >
                          Sign now
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'info' && <>

      {/* Header */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 text-xl font-bold">
              {employee.first_name[0]}{employee.last_name[0]}
            </div>
            {editing ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} placeholder="First name" className={inputClass + ' max-w-[160px]'} />
                  <input value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Last name" className={inputClass + ' max-w-[160px]'} />
                  <input value={editForm.preferred_name} onChange={e => setEditForm(f => ({ ...f, preferred_name: e.target.value }))} placeholder="Preferred name" className={inputClass + ' max-w-[140px]'} />
                </div>
                <div className="flex gap-2">
                  <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} placeholder="Job title" className={inputClass + ' max-w-[200px]'} />
                  <input value={editForm.department_name} onChange={e => setEditForm(f => ({ ...f, department_name: e.target.value }))} placeholder="Department" className={inputClass + ' max-w-[200px]'} />
                </div>
                <div className="flex gap-2 items-center">
                  <select value={editForm.employment_type} onChange={e => setEditForm(f => ({ ...f, employment_type: e.target.value }))} className={inputClass + ' max-w-[140px]'}>
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="intern">Intern</option>
                    <option value="temp">Temp</option>
                  </select>
                  <StatusBadge status={employee.employment_status} />
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-bold text-foreground">
                  {employee.first_name} {employee.last_name}
                  {employee.preferred_name && <span className="text-muted-foreground font-normal"> ({employee.preferred_name})</span>}
                </h2>
                <p className="text-sm text-muted-foreground">{employee.title} &middot; {employee.department_name ?? 'No Department'}</p>
                <div className="flex items-center gap-3 mt-2">
                  <StatusBadge status={employee.employment_status} />
                  <span className="text-xs text-muted-foreground capitalize">{employee.employment_type.replace('_', ' ')}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Contact Info */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Contact Information</h3>
          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Work Email</label>
                <input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Personal Email</label>
                <input value={editForm.personal_email} onChange={e => setEditForm(f => ({ ...f, personal_email: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Phone</label>
                <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Location</label>
                <input value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} className={inputClass} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="edit-remote" checked={editForm.remote} onChange={e => setEditForm(f => ({ ...f, remote: e.target.checked }))} className="rounded border-border" />
                <label htmlFor="edit-remote" className="text-sm text-foreground">Remote</label>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Manager</label>
                <input value={editForm.manager_name} onChange={e => setEditForm(f => ({ ...f, manager_name: e.target.value }))} className={inputClass} />
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-4 h-4" /> {employee.email}</div>
              {employee.personal_email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-4 h-4 opacity-50" /> {employee.personal_email} (personal)</div>}
              {employee.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-4 h-4" /> {employee.phone}</div>}
              {employee.location && <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="w-4 h-4" /> {employee.location} {employee.remote && '(Remote)'}</div>}
              <div className="flex items-center gap-2 text-muted-foreground"><Calendar className="w-4 h-4" /> Hired {employee.hire_date?.split('T')[0]}</div>
              {employee.manager_name && <div className="text-muted-foreground">Manager: {employee.manager_name}</div>}
            </div>
          )}
        </Card>

        {/* Skills */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Skills</h3>
          {editing ? (
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Skills (comma-separated)</label>
              <input value={editForm.skills} onChange={e => setEditForm(f => ({ ...f, skills: e.target.value }))} placeholder="Active Directory, Networking, M365" className={inputClass} />
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {employee.skills.length === 0 ? (
                <p className="text-xs text-muted-foreground">No skills listed</p>
              ) : (
                employee.skills.map(skill => (
                  <span key={skill} className="px-2 py-1 bg-surface-3 rounded-md text-xs text-foreground">{skill}</span>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Certifications */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> Certifications</h3>
          {employee.certifications.length === 0 ? (
            <p className="text-xs text-muted-foreground">No certifications</p>
          ) : (
            <div className="space-y-2">
              {employee.certifications.map(cert => (
                <div key={cert.name} className="flex items-center justify-between py-1">
                  <div>
                    <p className="text-sm text-foreground">{cert.name}</p>
                    <p className="text-xs text-muted-foreground">{cert.issuer}</p>
                  </div>
                  <StatusBadge status={cert.status} />
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Emergency Contacts */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Emergency Contacts</h3>
          {employee.emergency_contacts.length === 0 ? (
            <p className="text-xs text-muted-foreground">No emergency contacts</p>
          ) : (
            <div className="space-y-2">
              {employee.emergency_contacts.map((c, i) => (
                <div key={i} className="py-1">
                  <p className="text-sm text-foreground">{c.name} <span className="text-muted-foreground">({c.relationship})</span></p>
                  <p className="text-xs text-muted-foreground">{c.phone}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Notes */}
        {(editing || employee.notes) && (
          <Card>
            <h3 className="text-sm font-medium text-foreground mb-3">Notes</h3>
            {editing ? (
              <textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows={3} className={inputClass} placeholder="Internal notes..." />
            ) : (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{employee.notes}</p>
            )}
          </Card>
        )}
      </div>
      </>}
    </div>
  );
}
